/**
 * SchoolBehaviour — Student discipline incident tracking.
 * Props: tenantId
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Plus, Shield, TrendingUp, Users, Scan } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import IncidentReporter from '@/components/incidents/IncidentReporter';

const SEVERITY_COLORS = {
  low: 'bg-slate-500/20 text-slate-300 border-slate-400/30',
  medium: 'bg-amber-500/20 text-amber-700 border-amber-400',
  high: 'bg-orange-500/20 text-orange-700 border-orange-400',
  critical: 'bg-rose-500/20 text-rose-700 border-rose-500',
};

const STATUS_COLORS = {
  submitted: 'bg-sky-100 text-sky-800',
  under_review: 'bg-amber-100 text-amber-800',
  action_taken: 'bg-indigo-100 text-indigo-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  escalated: 'bg-rose-100 text-rose-800',
  appealed: 'bg-purple-100 text-purple-800',
  draft: 'bg-slate-100 text-slate-600',
};

const BASE = (tid) => `/school/tenants/${tid}/behaviour`;

const SchoolBehaviour = ({ tenantId }) => {
  const qc = useQueryClient();
  const [tab, setTab] = useState('incidents');
  const [createOpen, setCreateOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const { data: catalog } = useQuery({
    queryKey: ['behaviour-catalog', tenantId],
    queryFn: async () => (await api.get(`${BASE(tenantId)}/catalog`)).data,
  });
  const { data: incidents = [], isLoading } = useQuery({
    queryKey: ['behaviour-incidents', tenantId],
    queryFn: async () => (await api.get(`${BASE(tenantId)}/incidents`)).data,
  });
  const { data: stats } = useQuery({
    queryKey: ['behaviour-stats', tenantId],
    queryFn: async () => (await api.get(`${BASE(tenantId)}/stats?days=30`)).data,
  });
  const { data: studentsData } = useQuery({
    queryKey: ['school-students-list-min', tenantId],
    queryFn: async () => (await api.get(`/school/tenants/${tenantId}/students?limit=500`)).data,
  });
  const students = studentsData?.students || studentsData || [];

  return (
    <div className="space-y-5" data-testid="school-behaviour-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-indigo-600" /> Behaviour & Discipline
          </h2>
          <p className="text-sm text-slate-500 mt-1">Student incident reporting, tracking, and resolution</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setScanOpen(true)}
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="behaviour-scan-btn"
          >
            <Scan className="h-4 w-4 mr-1" /> Scan &amp; Report
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="behaviour-new-btn">
            <Plus className="h-4 w-4 mr-1" /> New Incident
          </Button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total (30d)" value={stats?.total ?? 0} icon={TrendingUp} color="indigo" testid="behaviour-stat-total" />
        <StatCard label="Open" value={stats?.open ?? 0} icon={AlertTriangle} color="amber" testid="behaviour-stat-open" />
        <StatCard label="Critical" value={stats?.critical ?? 0} icon={AlertTriangle} color="rose" testid="behaviour-stat-critical" />
        <StatCard label="Repeat Offender Flags" value={stats?.repeat_offender_flags ?? 0} icon={Users} color="orange" testid="behaviour-stat-repeat" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="incidents" data-testid="behaviour-tab-incidents">Incidents</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="behaviour-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="repeat" data-testid="behaviour-tab-repeat">Repeat Offenders</TabsTrigger>
        </TabsList>

        <TabsContent value="incidents" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left p-3">Student</th>
                    <th className="text-left p-3">Category</th>
                    <th className="text-left p-3">Severity</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Reported</th>
                    <th className="text-left p-3">When</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && <tr><td colSpan={6} className="p-5 text-center text-slate-500">Loading...</td></tr>}
                  {!isLoading && incidents.length === 0 && <tr><td colSpan={6} className="p-5 text-center text-slate-500">No incidents logged.</td></tr>}
                  {incidents.map(i => (
                    <tr key={i.id} onClick={() => setDetailId(i.id)} className="border-b cursor-pointer hover:bg-slate-50" data-testid={`behaviour-row-${i.id}`}>
                      <td className="p-3">
                        <p className="font-semibold">{i.student_name || '—'}</p>
                        <p className="text-xs text-slate-500">{i.admission_number}</p>
                        {i.repeat_offender && <Badge variant="outline" className="text-[10px] mt-1 bg-orange-50 text-orange-700 border-orange-300">Repeat offender</Badge>}
                      </td>
                      <td className="p-3 capitalize text-xs">{i.category?.replace(/_/g, ' ')}</td>
                      <td className="p-3"><Badge variant="outline" className={`capitalize ${SEVERITY_COLORS[i.severity]}`}>{i.severity}</Badge></td>
                      <td className="p-3"><Badge variant="outline" className={`capitalize ${STATUS_COLORS[i.status]}`}>{i.status?.replace('_', ' ')}</Badge></td>
                      <td className="p-3 text-xs">{i.reported_by_name || i.reported_by_email || '—'}</td>
                      <td className="p-3 text-xs text-slate-500">{i.occurred_at ? new Date(i.occurred_at).toLocaleString() : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="mt-4 grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader><CardTitle className="text-base">By Category</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(stats?.by_category || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span className="capitalize text-sm">{k.replace(/_/g, ' ')}</span>
                  <Badge>{v}</Badge>
                </div>
              ))}
              {Object.keys(stats?.by_category || {}).length === 0 && <p className="text-sm text-slate-500">No data.</p>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Class Hotspots</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(stats?.class_hotspots || []).map(c => (
                <div key={c.class_id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span className="text-sm">{c.class_name}</span>
                  <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300">{c.count} incidents</Badge>
                </div>
              ))}
              {(stats?.class_hotspots || []).length === 0 && <p className="text-sm text-slate-500">No hotspots.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="repeat" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b text-xs uppercase text-slate-500">
                  <tr><th className="text-left p-3">Student</th><th className="text-left p-3">Adm #</th><th className="text-right p-3">Incidents</th></tr>
                </thead>
                <tbody>
                  {(stats?.repeat_offenders || []).length === 0 && <tr><td colSpan={3} className="p-5 text-center text-slate-500">No repeat offenders.</td></tr>}
                  {(stats?.repeat_offenders || []).map(s => (
                    <tr key={s.student_id} className="border-b">
                      <td className="p-3 font-semibold">{s.name}</td>
                      <td className="p-3 text-xs text-slate-500">{s.admission_number}</td>
                      <td className="p-3 text-right"><Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300">{s.incident_count}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <NewIncidentDialog
        open={createOpen} onOpenChange={setCreateOpen}
        tenantId={tenantId} students={students} catalog={catalog}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['behaviour-incidents', tenantId] });
          qc.invalidateQueries({ queryKey: ['behaviour-stats', tenantId] });
        }}
      />
      {detailId && <IncidentDetailDialog tenantId={tenantId} incidentId={detailId} onClose={() => setDetailId(null)} catalog={catalog} />}

      {/* Unified scan-first reporter — uses the new ecosystem-wide /api/incidents */}
      <IncidentReporter
        open={scanOpen}
        onOpenChange={setScanOpen}
        contextType="school"
        contextId={tenantId}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['behaviour-incidents', tenantId] });
          qc.invalidateQueries({ queryKey: ['behaviour-stats', tenantId] });
        }}
      />
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color, testid }) => (
  <Card data-testid={testid}>
    <CardContent className="pt-5 pb-4 flex items-center justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-3xl font-bold">{value}</p>
      </div>
      <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
        <Icon className={`h-5 w-5 text-${color}-600`} />
      </div>
    </CardContent>
  </Card>
);

const NewIncidentDialog = ({ open, onOpenChange, tenantId, students, catalog, onCreated }) => {
  const [form, setForm] = useState({
    student_id: '', category: 'lateness', severity: 'low', description: '',
    location: '', immediate_action: '', follow_up_required: false, notify_parents: true,
  });
  const mut = useMutation({
    mutationFn: (data) => api.post(`${BASE(tenantId)}/incidents`, data).then(r => r.data),
    onSuccess: (r) => { toast.success(r.repeat_offender ? 'Incident logged (REPEAT offender detected)' : 'Incident logged'); onOpenChange(false); onCreated?.(); setForm({ ...form, student_id: '', description: '', severity: 'low' }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>New Behaviour Incident</DialogTitle>
          <DialogDescription>Report a student discipline or behaviour incident</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Student *</Label>
            <Select value={form.student_id} onValueChange={v => setForm({ ...form, student_id: v })}>
              <SelectTrigger data-testid="behaviour-form-student"><SelectValue placeholder="Select student" /></SelectTrigger>
              <SelectContent>
                {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.admission_number})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="behaviour-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(catalog?.categories || ['lateness']).map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Severity</Label>
              <Select value={form.severity} onValueChange={v => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="behaviour-form-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['low', 'medium', 'high', 'critical'].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description *</Label>
            <textarea className="w-full border rounded p-2 text-sm min-h-[90px]"
                      value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                      data-testid="behaviour-form-description" />
          </div>
          <div><Label>Location</Label>
            <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                   placeholder="Classroom, bus, hostel..." />
          </div>
          <div><Label>Immediate action taken</Label>
            <Input value={form.immediate_action} onChange={e => setForm({ ...form, immediate_action: e.target.value })} />
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.notify_parents} onCheckedChange={v => setForm({ ...form, notify_parents: !!v })} /> Notify parents (medium+)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.follow_up_required} onCheckedChange={v => setForm({ ...form, follow_up_required: !!v })} /> Follow-up needed
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => mut.mutate(form)}
                  disabled={mut.isPending || !form.student_id || !form.description}
                  className="bg-indigo-600 text-white" data-testid="behaviour-form-submit">
            {mut.isPending ? 'Logging...' : 'Log Incident'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const IncidentDetailDialog = ({ tenantId, incidentId, onClose, catalog }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['behaviour-detail', incidentId],
    queryFn: async () => (await api.get(`${BASE(tenantId)}/incidents/${incidentId}`)).data,
  });
  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState('none');
  const statusMut = useMutation({
    mutationFn: (status) => api.put(`${BASE(tenantId)}/incidents/${incidentId}/status`, { status, outcome, resolution_note: note }),
    onSuccess: (_, status) => { toast.success(`Marked ${status}`); qc.invalidateQueries({ queryKey: ['behaviour-incidents', tenantId] }); qc.invalidateQueries({ queryKey: ['behaviour-detail', incidentId] }); qc.invalidateQueries({ queryKey: ['behaviour-stats', tenantId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {isLoading ? <p className="p-5">Loading...</p> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <span>Incident</span>
                <Badge variant="outline" className={`capitalize ${SEVERITY_COLORS[data?.severity]}`}>{data?.severity}</Badge>
                <Badge variant="outline" className={`capitalize ${STATUS_COLORS[data?.status]}`}>{data?.status?.replace('_', ' ')}</Badge>
                {data?.repeat_offender && <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">Repeat</Badge>}
              </DialogTitle>
              <DialogDescription>
                <span className="capitalize">{data?.category?.replace(/_/g, ' ')}</span> · {data?.location || 'no location'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm bg-slate-50 rounded p-3 whitespace-pre-wrap">{data?.description}</p>
              {data?.immediate_action && <p className="text-xs text-slate-600">Immediate action: {data.immediate_action}</p>}
              {data?.outcome && <p className="text-xs text-slate-600">Outcome: <b className="capitalize">{data.outcome.replace(/_/g, ' ')}</b></p>}
              <p className="text-xs text-slate-500">Reported by {data?.reported_by_name || data?.reported_by_email} · {data?.occurred_at ? new Date(data.occurred_at).toLocaleString() : ''}</p>

              <div className="border-t pt-3">
                <Label>Resolution note</Label>
                <Input value={note} onChange={e => setNote(e.target.value)} className="mt-1" data-testid="behaviour-resolution-note" />
                <Label className="mt-3 block">Outcome</Label>
                <Select value={outcome} onValueChange={setOutcome}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(catalog?.outcomes || ['none']).map(o => <SelectItem key={o} value={o} className="capitalize">{o.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap gap-2 mt-3">
                  {['under_review', 'action_taken', 'resolved', 'escalated', 'appealed'].map(s => (
                    <Button key={s} size="sm" variant="outline"
                            onClick={() => statusMut.mutate(s)}
                            disabled={data?.status === s || statusMut.isPending}
                            data-testid={`behaviour-status-${s}`}>
                      <span className="capitalize">{s.replace('_', ' ')}</span>
                    </Button>
                  ))}
                </div>
              </div>

              {/* History */}
              <div className="border-t pt-3">
                <h4 className="font-semibold text-sm mb-2">History</h4>
                <div className="space-y-1 text-xs">
                  {(data?.history || []).map((h, i) => (
                    <div key={i} className="flex items-center gap-2 bg-slate-50 rounded p-1.5">
                      <Badge variant="outline" className="capitalize text-[10px]">{h.action?.replace('_', ' ')}</Badge>
                      <span>{h.by}</span>
                      <span className="text-slate-500 ml-auto">{h.at ? new Date(h.at).toLocaleString() : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SchoolBehaviour;
